import React, { useState, useEffect } from 'react';
import './RecordsOverview.css';
import { getRecords, deleteRecord, searchRecords, EvaluationRecord } from '../../services/api';
import { RecordDetailModal } from './RecordDetailModal';

export const RecordsOverview: React.FC = () => {
  const [records, setRecords] = useState<EvaluationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<EvaluationRecord | null>(null);
  const pageSize = 20;

  const loadRecords = async (page: number, keyword?: string) => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const response = keyword
        ? await searchRecords(keyword, skip, pageSize)
        : await getRecords(skip, pageSize);
      
      setRecords(response.records);
      setTotal(response.total);
    } catch (error) {
      console.error('加载记录失败:', error);
      alert('加载记录失败，请检查后端服务是否启动');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(currentPage, searchKeyword);
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadRecords(1, searchKeyword);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这条记录吗？')) {
      return;
    }

    try {
      await deleteRecord(id);
      alert('删除成功');
      loadRecords(currentPage, searchKeyword);
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败');
    }
  };

  const handleViewDetail = (record: EvaluationRecord) => {
    setSelectedRecord(record);
  };

  const handleCloseDetail = () => {
    setSelectedRecord(null);
  };

  const totalPages = Math.ceil(total / pageSize);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 80) return 'score-good';
    if (score >= 70) return 'score-fair';
    return 'score-poor';
  };

  const getSentimentLabel = (sentiment: string) => {
    const labels: Record<string, string> = {
      positive: '积极',
      neutral: '中性',
      negative: '消极',
    };
    return labels[sentiment] || sentiment;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="records-overview">
      <div className="records-header">
        <div className="records-title">
          <h1>质检总览</h1>
          <div className="records-stats">
            共 <span className="stat-number">{total}</span> 条记录
          </div>
        </div>
        
        <div className="records-search">
          <input
            type="text"
            placeholder="搜索文件名..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            搜索
          </button>
          {searchKeyword && (
            <button
              onClick={() => {
                setSearchKeyword('');
                setCurrentPage(1);
                loadRecords(1);
              }}
              className="clear-button"
            >
              清除
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="records-loading">
          <div className="loading-spinner" />
          <div>加载中...</div>
        </div>
      ) : records.length === 0 ? (
        <div className="records-empty">
          <div className="empty-icon">📋</div>
          <div className="empty-text">
            {searchKeyword ? '未找到匹配的记录' : '暂无质检记录'}
          </div>
          <div className="empty-hint">
            {!searchKeyword && '上传音频文件进行质检后，记录将显示在这里'}
          </div>
        </div>
      ) : (
        <>
          <div className="records-table-container">
            <table className="records-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>文件名</th>
                  <th>时长</th>
                  <th>综合评分</th>
                  <th>礼貌度</th>
                  <th>结构化</th>
                  <th>同理心</th>
                  <th>风险</th>
                  <th>情绪</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id}>
                    <td>{record.id}</td>
                    <td className="filename-cell" title={record.filename}>
                      {record.filename}
                    </td>
                    <td>{formatDuration(record.audio_duration)}</td>
                    <td>
                      <span className={`score-badge ${getScoreColor(record.overall_score)}`}>
                        {record.overall_score}
                      </span>
                    </td>
                    <td>
                      <span className="score-mini">{record.politeness_score}</span>
                    </td>
                    <td>
                      <span className="score-mini">{record.structure_score}</span>
                    </td>
                    <td>
                      <span className="score-mini">{record.empathy_score}</span>
                    </td>
                    <td>
                      <span className="score-mini">{record.risk_score}</span>
                    </td>
                    <td>
                      <span className={`sentiment-badge sentiment-${record.sentiment}`}>
                        {getSentimentLabel(record.sentiment)}
                      </span>
                    </td>
                    <td className="date-cell">{formatDate(record.created_at)}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleViewDetail(record)}
                        className="action-button view-button"
                      >
                        查看
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="action-button delete-button"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="records-pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                上一页
              </button>
              <div className="pagination-info">
                第 {currentPage} / {totalPages} 页
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}

      {selectedRecord && (
        <RecordDetailModal record={selectedRecord} onClose={handleCloseDetail} />
      )}
    </div>
  );
};
